import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card, Skeleton } from '@/components/ui';
import { EventsHero } from '@/components/events/EventsHero';
import { HomeEventCard } from '@/components/home/HomeEventCard';
import { useEvents, type EventListItem } from '@/hooks/useEvents';

/**
 * Events — listado COMPLETO de eventos disponibles (`/eventos`).
 *
 * ROUND9 UAT-C07: antes este contenido vivía en `/` (Home). Ahora `/` es
 * el landing curado y este es el catálogo completo de eventos. Se nutre de
 * `public.events` (backend real) vía `useEvents()`.
 *
 * REDISEÑO VISUAL (solo estético, SPEC-015): mismo lenguaje premium del Home —
 * hero oscuro inmersivo (`EventsHero`) con imagen real de concierto fundida, y
 * las MISMAS cards que "Eventos destacados" del Home (`HomeEventCard`). Cero
 * cambios de datos, hooks, rutas, orden ni copy (i18n `events` intacto).
 */
export function EventsPage(): JSX.Element {
  const { t } = useTranslation('events');
  const { data: events, isLoading, isError } = useEvents();

  // Fondo del hero: la portada REAL más representativa del catálogo (misma
  // heurística que el Home; local para no tocar el Home). Sin inventar assets.
  const heroImage = pickEventBackdrop(events ?? []);

  return (
    <div>
      <EventsHero title={t('list.heading')} subtitle={t('list.subtitle')} heroImage={heroImage} />

      <section className="container-fc py-12 md:py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted" role="alert">
              {t('list.error')}
            </p>
          </Card>
        ) : !events || events.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted">
              {t('list.emptyPrefix')}{' '}
              <Link to="/my-purchases" className="text-fc-ink underline hover:text-fc-ink/70">
                {t('list.emptyLink')}
              </Link>
              .
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((evt) => (
              <HomeEventCard key={evt.id} event={evt} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Portada de fondo del hero: la que más se repite entre los eventos (imagen
 * "de la casa" del catálogo → en el seed real, público en concierto). Misma
 * lógica que el Home; se duplica local para no modificar el Home. Sin portadas
 * → null (hero a gradiente).
 */
function pickEventBackdrop(events: ReadonlyArray<EventListItem>): string | null {
  const counts = new Map<string, number>();
  for (const e of events) {
    if (e.coverImage) counts.set(e.coverImage, (counts.get(e.coverImage) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [url, n] of counts) {
    if (n > bestCount) {
      best = url;
      bestCount = n;
    }
  }
  return best;
}

/** Skeleton que replica la silueta de `HomeEventCard` durante la carga. */
function EventCardSkeleton(): JSX.Element {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm">
      <Skeleton shape="aspect-[16/10] w-full rounded-none" srLabel="Cargando eventos…" />
      <div className="flex flex-1 flex-col gap-2 p-4 md:p-5">
        <Skeleton shape="h-5 w-4/5" srLabel="" />
        <Skeleton shape="h-4 w-2/3" srLabel="" />
        <div className="mt-4 flex items-center justify-between border-t border-fc-line/70 pt-4">
          <Skeleton shape="h-5 w-24" srLabel="" />
          <Skeleton shape="h-5 w-20" srLabel="" />
        </div>
      </div>
    </div>
  );
}
